import { Dropdown, Typography, Popover, Badge } from "antd";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import ArrowDown from "./../../assets/svgs/ArrowDown.svg";
import CompanySettingsIcon from "./../../assets/svgs/CompanySettingsIcon.svg";
import KeyIcon from "./../../assets/svgs/KeyIcon.svg";
import LogoutIcon from "./../../assets/svgs/LogoutIcon.svg";
import NotificationsIcon from "./../../assets/svgs/NavBarIcons/NotificationsIcon.svg";
import SearchIcon from "./../../assets/svgs/NavBarIcons/SearchIcon.svg";
import SettingsIcon from "./../../assets/svgs/NavBarIcons/SettingsIcon.svg";
import ProfileIconBlue from "./../../assets/svgs/ProfileIconBlue.svg";

import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import BalanceContext from "../../context/BalanceContext";
import { getUserType, handleLogout } from "../../helpers/userDetails";
import useAxiosPost from "../../hooks/useAxiosPost";
import CashInHandContainer from "../Items/CashAvaialble/Index";
import NotificationsDropdown, {
  NotificationItem,
  NotificationsResponseData,
} from "../Modals/NotificationsModal";

const navItems: NavItem[] = [
  // { label: "Settings", url: "/settings", icon: SettingsIcon },
  // { label: "Search", url: "/search", icon: SearchIcon },
  { label: "Notification", dataKey: "notifications", icon: NotificationsIcon },
];

const TopNavBar = () => {
  const { value, setValue } = useContext(BalanceContext);

  const [staff, setStaffProfile] = useState<StaffListType | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [notificationsPage, setNotificationsPage] = useState<number>(1);
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<ResponseProp>("auth/getMyProfile");
  const [
    postNotificationsApi,
    notificationsApiResponse,
    notificationsApiError,
    notificationsApiLoading,
    notificationsApiMessage,
    notificationsApiErrorCount,
  ] = useAxiosPost<ResponseProp & { page?: number }>(
    "notifications/getMyNotifications"
  );

  const [
    postMarkAsNotifiedApi,
    markAsNotifiedResponse,
    markAsNotifiedError,
    markAsNotifiedLoading,
    markAsNotifiedMessage,
    markAsNotifiedErrorCount,
  ] = useAxiosPost<ResponseProp & { notificationId: string }>(
    "notifications/markAsNotified"
  );

  const postNotificationsApiRef = useRef(postNotificationsApi);
  const notificationsPageRef = useRef(notificationsPage);
  const previousNotificationsRef = useRef<NotificationItem[]>([]);
  const beepAudioRef = useRef<HTMLAudioElement | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  useEffect(() => {
    postNotificationsApiRef.current = postNotificationsApi;
  }, [postNotificationsApi]);

  useEffect(() => {
    notificationsPageRef.current = notificationsPage;
  }, [notificationsPage]);

  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      setStaffProfile(data);
    }
  }, [apiResponse]);
  useEffect(() => {
    postApiData({});
  }, []);
  useEffect(() => {
    if (value > 0) {
      postApiData({});
    }
  }, [value]);

  useEffect(() => {
    if (notificationsOpen) {
      // When opening dropdown, show loading only if it's the first time
      if (isInitialLoadRef.current) {
        setIsInitialLoading(true);
      }
      postNotificationsApi({ page: notificationsPage });
    }
  }, [notificationsOpen, notificationsPage]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Initialize beep audio
  useEffect(() => {
    beepAudioRef.current = new Audio("/mixkit-correct-answer-tone-2870.wav");
    beepAudioRef.current.volume = 0.5; // Set volume to 50%
  }, []);

  // Polling mechanism - check for new notifications every 10 seconds
  useEffect(() => {
    const pollInterval = setInterval(() => {
      // Don't show loading during polling
      isInitialLoadRef.current = false;
      postNotificationsApiRef.current({ page: 1 });
    }, 10000); // Poll every 10 seconds

    // Initial fetch - show loading
    isInitialLoadRef.current = true;
    setIsInitialLoading(true);
    postNotificationsApiRef.current({ page: 1 });

    return () => clearInterval(pollInterval);
  }, []);

  // Hide loading after first response
  useEffect(() => {
    if (notificationsApiResponse !== null && isInitialLoadRef.current) {
      setIsInitialLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [notificationsApiResponse]);

  // Check for new notifications and show browser notification + beep
  useEffect(() => {
    if (
      notificationsApiResponse !== null &&
      notificationsApiResponse.responseData &&
      staff
    ) {
      const currentNotifications =
        (notificationsApiResponse.responseData as NotificationsResponseData)
          .notifications || [];
      const previousNotifications = previousNotificationsRef.current;
      const currentStaffId = staff.intPkHeadID;

      // Find notifications that need to be notified (notified === false AND viewed === false)
      const notificationsToNotify = currentNotifications.filter(
        (notification) => {
          const receiver = notification.notificationReceivers.find(
            (r) => r.receiverId === currentStaffId
          );
          return receiver && !receiver.notified && !receiver.viewed;
        }
      );

      // Check if these are truly new (not in previous list or were not notified before)
      const trulyNewNotifications = notificationsToNotify.filter((newNotif) => {
        const prevNotif = previousNotifications.find(
          (prev) => prev._id === newNotif._id
        );
        // If notification doesn't exist in previous list, it's new
        if (!prevNotif) {
          return true;
        }
        // If it exists but wasn't notified before, it's new
        const prevReceiver = prevNotif.notificationReceivers.find(
          (r) => r.receiverId === currentStaffId
        );
        return prevReceiver && !prevReceiver.notified;
      });

      // Show browser notification and beep for each new notification
      trulyNewNotifications.forEach((notification) => {
        // Play beep sound
        if (beepAudioRef.current) {
          beepAudioRef.current.play().catch((error) => {
            console.log("Error playing beep sound:", error);
          });
        }

        // Show browser notification
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(notification.notificationTitle, {
            body: notification.notificationMessage,
            icon: "/LogoBlue.ico",
            badge: "/LogoBlue.ico",
            tag: notification._id, // Prevent duplicate notifications
          });
        }

        // Mark as notified in backend
        postMarkAsNotifiedApi({ notificationId: notification._id });
      });

      // Update previous notifications
      previousNotificationsRef.current = currentNotifications;
    }
  }, [notificationsApiResponse, staff]);

  const handleNotificationRead = useCallback(() => {
    // Refresh notifications after marking as read
    postNotificationsApiRef.current({ page: notificationsPageRef.current });
  }, []);

  const handleNotificationsPageChange = (page: number) => {
    setNotificationsPage(page);
  };

  const getNotificationsData = () => {
    if (
      notificationsApiResponse !== null &&
      notificationsApiResponse.responseData
    ) {
      return notificationsApiResponse.responseData as NotificationsResponseData;
    }
    return null;
  };

  const notificationsData = getNotificationsData();
  
  // Get unread count from API response
  const unreadCount = notificationsData?.unreadCount || 0;
  const navigate = useNavigate();
  const connectionOptionItems: MenuProps["items"] = [
    {
      key: "PROFILE",
      label: (
        <Typography className="profile-drop-down-item">
          <img src={ProfileIconBlue} />
          User Profile
        </Typography>
      ),
    },
    {
      key: "CHANGE_PASSWORD",
      label: (
        <Typography className="profile-drop-down-item">
          <img src={KeyIcon} />
          Change Password
        </Typography>
      ),
    },
    {
      key: "LOGOUT",
      label: (
        <Typography className="profile-drop-down-item">
          <img src={LogoutIcon} />
          Logout
        </Typography>
      ),
    },
  ];
  if (getUserType() === "CEO") {
    connectionOptionItems.splice(2, 0, {
      key: "COMPANY_SETTINGS",
      label: (
        <Typography className="profile-drop-down-item">
          <img src={CompanySettingsIcon} />
          Company Settings
        </Typography>
      ),
    });
  }
  const handleMenuClick = (e: any) => {
    if (e.key === "PROFILE") {
      navigate("/auth/profile");
    } else if (e.key === "COMPANY_SETTINGS") {
      navigate(`/auth/company-settings`);
    } else if (e.key === "CHANGE_PASSWORD") {
      navigate(`/auth/update-password`);
    } else if (e.key === "LOGOUT") {
      handleLogout();
    } else if (e.key === "LOGOUT") {
      handleLogout();
    }
  };
  const showName = (firstName: string, lastName: string, role: string) => {
    const fullName = `${firstName} ${lastName}`;
    if (fullName.length > 13) {
      return `${firstName} (${role})`;
    }
    return `${fullName} (${role})`;
  };
  return (
    <>
      <div className="top-nav-bar-items-container">
        {staff && ["RMAN", "CASHIER"].includes(staff.headType) && (
          <div style={{ marginRight: "40px" }}>
            <CashInHandContainer
              value={`${staff.userBalance.cashAvailable}`}
              label="Balance:"
            />
          </div>
        )}
        {navItems.map((item: NavItem) => {
          if (item.dataKey === "notifications") {
            return (
              <Popover
                key={item.dataKey}
                content={
                  <NotificationsDropdown
                    notifications={notificationsData?.notifications || null}
                    isLoading={isInitialLoading && notificationsApiLoading}
                    currentPage={notificationsData?.currentPage || 1}
                    totalPages={notificationsData?.totalPages || 0}
                    totalCount={notificationsData?.totalCount || 0}
                    pageSize={notificationsData?.pageSize || 10}
                    onPageChange={handleNotificationsPageChange}
                    onClose={() => setNotificationsOpen(false)}
                    onNotificationRead={handleNotificationRead}
                    currentStaffId={staff?.intPkHeadID}
                  />
                }
                trigger="click"
                placement="bottomRight"
                open={notificationsOpen}
                onOpenChange={setNotificationsOpen}
                overlayClassName="notifications-popover"
              >
                <div className="top-nav-bar-item notification-icon-container" style={{ cursor: "pointer", position: "relative",}}>
                  <img src={item.icon} alt="Notifications" />
                  {unreadCount > 0 && (
                    <span className="notification-badge-custom">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
              </Popover>
            );
          }
          return (
            <div key={item.dataKey} className="top-nav-bar-item">
              {<img src={item.icon} />}
            </div>
          );
        })}

        {/* <Select
          className="top-nav-select-control"
          size={"middle"}
          defaultValue="Hamza Arshad"
          //   onChange={handleChange}
          style={{ width: 200 }}
          options={options}
        /> */}
        <Dropdown
          menu={{
            items: connectionOptionItems,

            onClick: handleMenuClick,
          }}
          placement="bottomLeft"
        >
          <div className="user-profile-options-container">
            <div className="user-profile-container-with-profile-icon">
              <img src={ProfileIconBlue} />{" "}
              <Typography className="user-name-options-label">
                {staff &&
                  showName(staff.firstName, staff.lastName, staff.headType)}
              </Typography>{" "}
            </div>
            <img className="profile-arrow-down" src={ArrowDown} />
          </div>
        </Dropdown>
      </div>
    </>
  );
};

export default TopNavBar;
